import type { Hewan, Jalur, KelasSettings, LearningModule } from '../types';
import { readCompleted } from './progress';

/**
 * What one classroom's panel remembers across the week.
 *
 * Three things, and all of them belong to the class rather than to a child:
 *
 *   settings   what the teacher set in Mode Guru - children at the board, and
 *              which word the NAM lines use
 *   jalur      per lesson, the path chosen and any screens toggled by hand
 *   halaman    Halaman Kita: every animal the class coloured, with its name
 *
 * The spec asks for the panel's own file storage, not browser storage, because
 * "the class's week disappears on the first app update". This is localStorage,
 * because that is what a PWA has; the Capacitor build can move it to the
 * Filesystem plugin behind these same functions. Until then the warning stands
 * and is written in `docs/`: test persistence across a restart before a pilot.
 *
 * Guarded like `progress.ts`: private mode, a full disk and corrupt JSON all
 * read as "nothing saved yet" rather than taking a lesson down in front of a
 * class.
 */

const KEY = {
  settings: 'ifp.kelas.settings',
  halaman: 'ifp.kelas.halaman',
  jalur: (moduleId: string) => `ifp.kelas.jalur.${moduleId}`,
  bubbles: (moduleId: string) => `ifp.kelas.gelembung.${moduleId}`,
};

function read<T>(key: string, fallback: T, check: (value: unknown) => value is T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed: unknown = JSON.parse(raw);
    return check(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Full or blocked: it holds for this session and is gone on reload.
  }
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  Boolean(v) && typeof v === 'object' && !Array.isArray(v);

/* --- Settings ------------------------------------------------------------- */

export const DEFAULT_SETTINGS: KelasSettings = { anak: 4, nam: 'Tuhan' };

export function readSettings(): KelasSettings {
  const raw = read(KEY.settings, {}, isObject);
  const anak = Number(raw['anak']);
  return {
    anak: anak >= 1 && anak <= 4 ? Math.round(anak) : DEFAULT_SETTINGS.anak,
    nam: raw['nam'] === 'Allah' ? 'Allah' : 'Tuhan',
  };
}

export function writeSettings(settings: KelasSettings): void {
  write(KEY.settings, settings);
}

/** `{Tuhan}` in a line becomes the teacher's choice. */
export function resolveNam(text: string, settings: KelasSettings): string {
  return text.replace(/\{Tuhan\}/g, settings.nam);
}

/* --- Paths ------------------------------------------------------------------ */

export interface JalurState {
  jalur: Jalur;
  /** Screen numbers switched on. Starts as the path's list; the teacher may edit. */
  layar: number[];
}

export function screensFor(module: LearningModule, jalur: Jalur, all: number[]): number[] {
  if (jalur === 'lengkap' || !module.jalur) return [...all];
  return module.jalur[jalur].filter((n) => all.includes(n));
}

export function readJalur(module: LearningModule, all: number[]): JalurState {
  const raw = read(KEY.jalur(module.id), {}, isObject);
  const jalur: Jalur =
    raw['jalur'] === 'singkat' || raw['jalur'] === 'lengkap' ? raw['jalur'] : 'inti';
  const saved = Array.isArray(raw['layar'])
    ? raw['layar'].filter((n): n is number => typeof n === 'number' && all.includes(n))
    : null;
  return { jalur, layar: saved && saved.length > 0 ? saved : screensFor(module, jalur, all) };
}

export function writeJalur(moduleId: string, state: JalurState): void {
  write(KEY.jalur(moduleId), state);
}

/* --- Halaman Kita -------------------------------------------------------- */

export interface Makhluk {
  id: string;
  hewan: Hewan;
  moduleId: string;
  /** Which of the four lanes it was coloured in - "cari punyamu" uses it. */
  lane: number;
  /** region -> colour, exactly as the child left it. */
  painting: Record<string, string>;
  nama?: string;
  dibuat: number;
}

const isMakhluk = (v: unknown): v is Makhluk =>
  isObject(v) &&
  typeof v['id'] === 'string' &&
  typeof v['hewan'] === 'string' &&
  typeof v['moduleId'] === 'string' &&
  isObject(v['painting']);

export function readHalaman(): Makhluk[] {
  const raw = read<unknown[]>(KEY.halaman, [], Array.isArray);
  return raw.filter(isMakhluk);
}

export function addMakhluk(makhluk: Omit<Makhluk, 'id' | 'dibuat'>): Makhluk {
  const entry: Makhluk = {
    ...makhluk,
    id: `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    dibuat: Date.now(),
  };
  write(KEY.halaman, [...readHalaman(), entry]);
  window.dispatchEvent(new CustomEvent('kelas:halaman'));
  return entry;
}

export function nameMakhluk(id: string, nama: string): void {
  write(
    KEY.halaman,
    readHalaman().map((m) => (m.id === id ? { ...m, nama } : m)),
  );
  window.dispatchEvent(new CustomEvent('kelas:halaman'));
}

export function clearHalaman(): void {
  write(KEY.halaman, []);
  window.dispatchEvent(new CustomEvent('kelas:halaman'));
}

/**
 * Animals this class has met on this panel: a lesson with any screen done, or
 * an animal of that kind already living in the yard.
 *
 * The spec leans on this in three places - "Kucing kemarin punya berapa kaki?"
 * (Hari 2), the comparison silhouettes (Hari 3 Layar 5) and Tiga Sahabat - and
 * is firm that none of them may ask about an animal the class never met.
 */
export function hasMet(hewan: Hewan, moduleId: string): boolean {
  if (readCompleted(moduleId).size > 0) return true;
  return readHalaman().some((m) => m.hewan === hewan);
}

/* --- Layar 2's word bubbles ---------------------------------------------- */

export interface Gelembung {
  id: string;
  label: string;
  /** Ticked when the lesson has shown it true. Layar 2 is opened again at the end. */
  terbukti: boolean;
  /**
   * A word the teacher wrote by hand on the pad rather than picked: the ink as
   * one SVG path, and the box it sits in. `label` then only names it for a
   * screen reader. The panel has no keyboard, so this is the only free input.
   */
  tulisan?: { d: string; box: string };
}

export function readBubbles(moduleId: string): Gelembung[] {
  return read<unknown[]>(KEY.bubbles(moduleId), [], Array.isArray).filter(
    (g): g is Gelembung => isObject(g) && typeof g['id'] === 'string' && typeof g['label'] === 'string',
  );
}

export function writeBubbles(moduleId: string, bubbles: Gelembung[]): void {
  write(KEY.bubbles(moduleId), bubbles);
}
